/**
 * Traer un dibujo de afuera y convertirlo en el símbolo de una pieza.
 *
 * Hasta hoy `ModeloMueble.simbolo` era una LLAVE a `SIMBOLOS`, el mapa de ~30
 * funciones dibujadas a mano. Eso alcanzaba mientras el catálogo lo escribíamos
 * nosotros, pero deja al usuario sin salida: una pieza guardada por él tenía que
 * pedir prestado el dibujo de otra, y la vitrina mostraba un sofá donde el
 * usuario había guardado un lavatorio.
 *
 * Acá nace la otra mitad: un objeto puede traer su PROPIO trazo. Entra un SVG o
 * un PDF, sale un `Trazo` —caminos en `d` crudo más la caja que los contiene—, y
 * `SimboloMueble` lo estira a la medida de la pieza.
 *
 * Dos decisiones que mandan sobre todo el archivo:
 *
 * 1 · **Se guarda el camino, no el pixel.** Un raster se vería mal al estirarlo
 *     a 1,50 m y pesaría en el localStorage; un camino se escala sin perder
 *     nada. Por eso los dos lectores terminan en lo mismo: `d` de SVG.
 *
 * 2 · **Se normaliza la CAJA, no las coordenadas.** Los números quedan como
 *     vinieron y la caja viaja al lado. Así el `d` es auditable contra el
 *     original —se puede pegar en cualquier visor y se ve igual— y el encuadre
 *     es una multiplicación en el render, no una reescritura destructiva.
 */

/** Un dibujo traído de afuera, listo para encuadrarse en cualquier medida. */
export interface Trazo {
  /** Caminos SVG en `d`, ya absolutos y con sólo `M`/`L`/`C`/`Z`. */
  d: string[];
  /** Lo que ocupan esos caminos: `[x, y, ancho, alto]` en sus propias unidades. */
  caja: [number, number, number, number];
}

/**
 * Tope de caminos por símbolo.
 *
 * Una página de plano trae decenas de miles —la que medimos tenía 26 561— y eso
 * no es un símbolo, es un plano entero. Un mueble razonable no pasa de unos
 * cientos. Cortar acá no es tacañería: un objeto con 20 000 caminos haría
 * inusable la vitrina, que los dibuja todos en miniatura y de a decenas.
 */
export const TOPE_CAMINOS = 1500;

// ─── Matrices ───────────────────────────────────────────────────────────────
// `[a, b, c, d, e, f]`, la misma forma que usan SVG y PDF.

type Matriz = [number, number, number, number, number, number];

const IDENTIDAD: Matriz = [1, 0, 0, 1, 0, 0];

function por(m: Matriz, n: Matriz): Matriz {
  return [
    m[0] * n[0] + m[2] * n[1], m[1] * n[0] + m[3] * n[1],
    m[0] * n[2] + m[2] * n[3], m[1] * n[2] + m[3] * n[3],
    m[0] * n[4] + m[2] * n[5] + m[4], m[1] * n[4] + m[3] * n[5] + m[5],
  ];
}

const aplicar = (m: Matriz, x: number, y: number): [number, number] =>
  [m[0] * x + m[2] * y + m[4], m[1] * x + m[3] * y + m[5]];

// ─── Segmentos ──────────────────────────────────────────────────────────────
// La forma interna: todo se reduce a mover, recta, cúbica y cerrar. Arcos y
// cuadráticas se convierten al entrar, así hay UN solo caso que transformar,
// medir y escribir.

type Seg =
  | { o: 'M'; p: [number, number] }
  | { o: 'L'; p: [number, number] }
  | { o: 'C'; p: [number, number, number, number, number, number] }
  | { o: 'Z' };

/** Redondeo a 4 decimales: suficiente para 1 µm sobre un metro, y sin ruido. */
const n4 = (v: number) => Math.round(v * 1e4) / 1e4;

function transformar(segs: Seg[], m: Matriz): Seg[] {
  if (m === IDENTIDAD) return segs;
  return segs.map((s): Seg => {
    if (s.o === 'Z') return s;
    if (s.o === 'C') {
      const [a, b, c, d, e, f] = s.p;
      const [p1x, p1y] = aplicar(m, a, b);
      const [p2x, p2y] = aplicar(m, c, d);
      const [p3x, p3y] = aplicar(m, e, f);
      return { o: 'C', p: [p1x, p1y, p2x, p2y, p3x, p3y] };
    }
    return { o: s.o, p: aplicar(m, s.p[0], s.p[1]) };
  });
}

function escribir(segs: Seg[]): string {
  const t: string[] = [];
  for (const s of segs) {
    if (s.o === 'Z') { t.push('Z'); continue; }
    t.push(s.o + s.p.map(n4).join(' '));
  }
  return t.join(' ');
}

/**
 * La caja de un grupo de segmentos, con los extremos REALES de las cúbicas.
 *
 * El atajo de siempre —encajar los puntos de control— da una caja más grande
 * que el dibujo, y acá eso no es un detalle: la caja es la que encuadra, así que
 * una caja inflada deja el símbolo chiquito y descentrado dentro de su pieza.
 */
function cajaDe(segs: Seg[]): [number, number, number, number] | null {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  const come = (x: number, y: number) => {
    if (x < x0) x0 = x; if (x > x1) x1 = x;
    if (y < y0) y0 = y; if (y > y1) y1 = y;
  };
  let px = 0, py = 0;
  for (const s of segs) {
    if (s.o === 'Z') continue;
    if (s.o === 'C') {
      const [ax, ay, bx, by, cx, cy] = s.p;
      come(cx, cy);
      // Los extremos de una cúbica están donde su derivada se anula. Se resuelve
      // por eje: una cuadrática por cada uno, y sólo valen las raíces en [0,1].
      for (const [p0, p1, p2, p3, eje] of
           [[px, ax, bx, cx, 0], [py, ay, by, cy, 1]] as const) {
        const a = -p0 + 3 * p1 - 3 * p2 + p3;
        const b = 2 * (p0 - 2 * p1 + p2);
        const c = p1 - p0;
        const raices = Math.abs(a) < 1e-12
          ? (Math.abs(b) < 1e-12 ? [] : [-c / b])
          : (() => {
              const disc = b * b - 4 * a * c;
              if (disc < 0) return [];
              const r = Math.sqrt(disc);
              return [(-b + r) / (2 * a), (-b - r) / (2 * a)];
            })();
        for (const t of raices) {
          if (!(t > 0 && t < 1)) continue;
          const u = 1 - t;
          const v = u * u * u * p0 + 3 * u * u * t * p1 + 3 * u * t * t * p2 + t * t * t * p3;
          if (eje === 0) come(v, py); else come(px, v);
        }
      }
      px = cx; py = cy;
    } else {
      come(s.p[0], s.p[1]);
      px = s.p[0]; py = s.p[1];
    }
  }
  if (!Number.isFinite(x0) || (x1 - x0 <= 0 && y1 - y0 <= 0)) return null;
  return [x0, y0, x1 - x0, y1 - y0];
}

// ─── Lector de `d` ──────────────────────────────────────────────────────────

const NUM = /[+-]?(?:\d*\.\d+|\d+\.?)(?:[eE][+-]?\d+)?/g;

/**
 * De `d` crudo a segmentos absolutos.
 *
 * Soporta la gramática entera (`MLHVCSQTAZ`, relativas incluidas) porque un SVG
 * exportado de Illustrator o Inkscape usa todo y a nadie le sirve un importador
 * que falle en silencio sobre la mitad del dibujo. Las cuadráticas y los arcos
 * se convierten a cúbicas acá mismo: río arriba hay un solo caso que mantener.
 */
function leerD(d: string): Seg[] {
  const segs: Seg[] = [];
  let x = 0, y = 0;            // dónde está la pluma
  let ix = 0, iy = 0;          // dónde empezó el sub-camino, para `Z`
  let cx = 0, cy = 0;          // reflejo para `S` / `T`
  let previo = '';

  const pasos = d.match(/[MmLlHhVvCcSsQqTtAaZz][^MmLlHhVvCcSsQqTtAaZz]*/g) ?? [];
  for (const paso of pasos) {
    const op = paso[0];
    const rel = op === op.toLowerCase() && op !== 'z' && op !== 'Z';
    const v = (paso.slice(1).match(NUM) ?? []).map(Number);
    const cuantos = { M: 2, L: 2, H: 1, V: 1, C: 6, S: 4, Q: 4, T: 2, A: 7, Z: 0 }[op.toUpperCase()] ?? 0;

    if (cuantos === 0) {
      segs.push({ o: 'Z' });
      x = ix; y = iy; previo = 'Z';
      continue;
    }
    for (let i = 0; i + cuantos <= v.length; i += cuantos) {
      const a = v.slice(i, i + cuantos);
      const O = op.toUpperCase();
      // `M` repetido son `L`; `m` repetido son `l`. Lo dice la especificación y
      // los exportadores lo usan a mansalva para ahorrar bytes.
      const real = O === 'M' && i > 0 ? 'L' : O;

      if (real === 'M' || real === 'L') {
        x = rel ? x + a[0] : a[0];
        y = rel ? y + a[1] : a[1];
        segs.push({ o: real, p: [x, y] });
        if (real === 'M') { ix = x; iy = y; }
      } else if (real === 'H') {
        x = rel ? x + a[0] : a[0];
        segs.push({ o: 'L', p: [x, y] });
      } else if (real === 'V') {
        y = rel ? y + a[0] : a[0];
        segs.push({ o: 'L', p: [x, y] });
      } else if (real === 'C' || real === 'S') {
        const [c1x, c1y] = real === 'C'
          ? [rel ? x + a[0] : a[0], rel ? y + a[1] : a[1]]
          : ('CS'.includes(previo) ? [2 * x - cx, 2 * y - cy] : [x, y]);
        const o = real === 'C' ? 2 : 0;
        const c2x = rel ? x + a[o] : a[o];
        const c2y = rel ? y + a[o + 1] : a[o + 1];
        const ex = rel ? x + a[o + 2] : a[o + 2];
        const ey = rel ? y + a[o + 3] : a[o + 3];
        segs.push({ o: 'C', p: [c1x, c1y, c2x, c2y, ex, ey] });
        cx = c2x; cy = c2y; x = ex; y = ey;
      } else if (real === 'Q' || real === 'T') {
        const [qx, qy] = real === 'Q'
          ? [rel ? x + a[0] : a[0], rel ? y + a[1] : a[1]]
          : ('QT'.includes(previo) ? [2 * x - cx, 2 * y - cy] : [x, y]);
        const o = real === 'Q' ? 2 : 0;
        const ex = rel ? x + a[o] : a[o];
        const ey = rel ? y + a[o + 1] : a[o + 1];
        // Una cuadrática ES una cúbica: los controles van a dos tercios.
        segs.push({ o: 'C', p: [
          x + (2 / 3) * (qx - x), y + (2 / 3) * (qy - y),
          ex + (2 / 3) * (qx - ex), ey + (2 / 3) * (qy - ey), ex, ey,
        ] });
        cx = qx; cy = qy; x = ex; y = ey;
      } else if (real === 'A') {
        const ex = rel ? x + a[5] : a[5];
        const ey = rel ? y + a[6] : a[6];
        for (const c of arcoACubicas(x, y, a[0], a[1], a[2], !!a[3], !!a[4], ex, ey)) {
          segs.push({ o: 'C', p: c });
        }
        x = ex; y = ey;
      }
      previo = real;
    }
  }
  return segs;
}

/**
 * Arco elíptico a cúbicas.
 *
 * Es la conversión del apéndice F de la especificación de SVG: se pasa de la
 * forma por EXTREMOS que usa `A` a la forma por CENTRO, y después se parte el
 * barrido en tramos de a lo sumo 90° porque una cúbica no aproxima bien más que
 * eso. Está acá —y no evitado— porque todo mueble redondo que venga de un
 * exportador real llega en arcos: lavatorios, inodoros, mesas.
 */
function arcoACubicas(
  x0: number, y0: number, rx: number, ry: number, giro: number,
  grande: boolean, barrido: boolean, x1: number, y1: number,
): Array<[number, number, number, number, number, number]> {
  // Radio cero: la especificación dice que el arco degenera en una recta.
  if (!rx || !ry) return [[x0, y0, x1, y1, x1, y1]];
  const r = (giro * Math.PI) / 180;
  const cos = Math.cos(r), sin = Math.sin(r);
  const dx = (x0 - x1) / 2, dy = (y0 - y1) / 2;
  const ux = cos * dx + sin * dy, uy = -sin * dx + cos * dy;
  rx = Math.abs(rx); ry = Math.abs(ry);
  // Radios demasiado chicos para unir los extremos: la especificación manda
  // agrandarlos hasta que alcancen, no descartar el arco.
  const exceso = (ux * ux) / (rx * rx) + (uy * uy) / (ry * ry);
  if (exceso > 1) { const k = Math.sqrt(exceso); rx *= k; ry *= k; }

  const den = rx * rx * uy * uy + ry * ry * ux * ux;
  const num = rx * rx * ry * ry - den;
  const k = Math.sqrt(Math.max(0, num / den)) * (grande === barrido ? -1 : 1);
  const vx = (k * rx * uy) / ry, vy = (-k * ry * ux) / rx;
  const ox = cos * vx - sin * vy + (x0 + x1) / 2;
  const oy = sin * vx + cos * vy + (y0 + y1) / 2;

  const ang = (px: number, py: number) => Math.atan2((py - vy) / ry, (px - vx) / rx);
  const a0 = ang(ux, uy);
  let da = ang(-ux, -uy) - a0;
  if (!barrido && da > 0) da -= 2 * Math.PI;
  if (barrido && da < 0) da += 2 * Math.PI;

  const tramos = Math.max(1, Math.ceil(Math.abs(da) / (Math.PI / 2)));
  const paso = da / tramos;
  // El factor que hace que una cúbica calque un arco de `paso` radianes.
  const t = (4 / 3) * Math.tan(paso / 4);
  const fuera = (a: number): [number, number] => {
    const px = rx * Math.cos(a), py = ry * Math.sin(a);
    return [cos * px - sin * py + ox, sin * px + cos * py + oy];
  };
  const salida: Array<[number, number, number, number, number, number]> = [];
  for (let i = 0; i < tramos; i++) {
    const ai = a0 + i * paso, af = ai + paso;
    const [pix, piy] = fuera(ai), [pfx, pfy] = fuera(af);
    const d1x = -rx * Math.sin(ai), d1y = ry * Math.cos(ai);
    const d2x = -rx * Math.sin(af), d2y = ry * Math.cos(af);
    const gira = (gx: number, gy: number): [number, number] =>
      [cos * gx - sin * gy, sin * gx + cos * gy];
    const [t1x, t1y] = gira(d1x, d1y), [t2x, t2y] = gira(d2x, d2y);
    salida.push([pix + t * t1x, piy + t * t1y, pfx - t * t2x, pfy - t * t2y, pfx, pfy]);
  }
  return salida;
}

// ─── SVG ────────────────────────────────────────────────────────────────────

/**
 * Lo único que se mira de un SVG importado.
 *
 * Es lista BLANCA y no lista negra a propósito: un SVG es XML ejecutable —trae
 * `<script>`, `<foreignObject>`, `<image>` con `href` remoto, `<use>` apuntando
 * afuera— y acá lo está subiendo un usuario para que lo vean todos los demás.
 * Nombrar lo que entra deja lo demás afuera aunque mañana aparezca una etiqueta
 * que hoy no existe.
 */
const PERMITIDAS = ['path', 'line', 'rect', 'circle', 'ellipse', 'polyline', 'polygon'];

/** Las primitivas se convierten a camino: después hay un solo tipo que tratar. */
function primitivaASegs(tag: string, leer: (a: string) => string | null): Seg[] {
  const el = { getAttribute: leer };
  const f = (a: string) => Number(el.getAttribute(a) ?? 0) || 0;
  const puntos = () => (el.getAttribute('points')?.match(NUM) ?? []).map(Number);

  switch (tag) {
    case 'path':
      return leerD(el.getAttribute('d') ?? '');
    case 'line':
      return [{ o: 'M', p: [f('x1'), f('y1')] }, { o: 'L', p: [f('x2'), f('y2')] }];
    case 'rect': {
      const [x, y, w, h] = [f('x'), f('y'), f('width'), f('height')];
      if (w <= 0 || h <= 0) return [];
      // Las esquinas redondeadas se respetan: un `rect` con `rx` es la forma
      // más común de dibujar un mueble, y cuadrarlo cambiaría el dibujo.
      const rx = Math.min(Number(el.getAttribute('rx') ?? el.getAttribute('ry') ?? 0) || 0, w / 2);
      const ry = Math.min(Number(el.getAttribute('ry') ?? el.getAttribute('rx') ?? 0) || 0, h / 2);
      if (rx <= 0 || ry <= 0) {
        return [{ o: 'M', p: [x, y] }, { o: 'L', p: [x + w, y] },
                { o: 'L', p: [x + w, y + h] }, { o: 'L', p: [x, y + h] }, { o: 'Z' }];
      }
      return leerD(
        `M${x + rx} ${y}H${x + w - rx}A${rx} ${ry} 0 0 1 ${x + w} ${y + ry}` +
        `V${y + h - ry}A${rx} ${ry} 0 0 1 ${x + w - rx} ${y + h}` +
        `H${x + rx}A${rx} ${ry} 0 0 1 ${x} ${y + h - ry}` +
        `V${y + ry}A${rx} ${ry} 0 0 1 ${x + rx} ${y}Z`);
    }
    case 'circle': {
      const r = f('r');
      if (r <= 0) return [];
      return leerD(`M${f('cx') - r} ${f('cy')}a${r} ${r} 0 1 0 ${r * 2} 0a${r} ${r} 0 1 0 ${-r * 2} 0Z`);
    }
    case 'ellipse': {
      const [rx, ry] = [f('rx'), f('ry')];
      if (rx <= 0 || ry <= 0) return [];
      return leerD(`M${f('cx') - rx} ${f('cy')}a${rx} ${ry} 0 1 0 ${rx * 2} 0a${rx} ${ry} 0 1 0 ${-rx * 2} 0Z`);
    }
    case 'polyline':
    case 'polygon': {
      const p = puntos();
      if (p.length < 4) return [];
      const segs: Seg[] = [{ o: 'M', p: [p[0], p[1]] }];
      for (let i = 2; i + 1 < p.length; i += 2) segs.push({ o: 'L', p: [p[i], p[i + 1]] });
      if (tag === 'polygon') segs.push({ o: 'Z' });
      return segs;
    }
    default:
      return [];
  }
}

/** `transform="translate(…) rotate(…)"` a matriz. */
function leerTransform(t: string | null): Matriz {
  if (!t) return IDENTIDAD;
  let m = IDENTIDAD;
  for (const [, nombre, crudo] of t.matchAll(/(\w+)\s*\(([^)]*)\)/g)) {
    const a = (crudo.match(NUM) ?? []).map(Number);
    const r = (g: number) => (g * Math.PI) / 180;
    let paso: Matriz | null = null;
    if (nombre === 'matrix' && a.length >= 6) paso = a.slice(0, 6) as Matriz;
    else if (nombre === 'translate') paso = [1, 0, 0, 1, a[0] ?? 0, a[1] ?? 0];
    else if (nombre === 'scale') paso = [a[0] ?? 1, 0, 0, a[1] ?? a[0] ?? 1, 0, 0];
    else if (nombre === 'rotate') {
      const g: Matriz = [Math.cos(r(a[0] ?? 0)), Math.sin(r(a[0] ?? 0)),
                         -Math.sin(r(a[0] ?? 0)), Math.cos(r(a[0] ?? 0)), 0, 0];
      // `rotate(a cx cy)` gira alrededor de un punto: trasladar, girar, volver.
      paso = a.length >= 3
        ? por(por([1, 0, 0, 1, a[1], a[2]], g), [1, 0, 0, 1, -a[1], -a[2]])
        : g;
    } else if (nombre === 'skewX') paso = [1, 0, Math.tan(r(a[0] ?? 0)), 1, 0, 0];
    else if (nombre === 'skewY') paso = [1, Math.tan(r(a[0] ?? 0)), 0, 1, 0, 0];
    if (paso) m = por(m, paso);
  }
  return m;
}

/**
 * Un nodo de dibujo, sin DOM: etiqueta, atributos e hijos.
 *
 * Existe porque el motor de abajo lo usan DOS entradas que no se parecen en
 * nada: un archivo SVG (donde los nodos son `Element` del navegador) y el árbol
 * de React que `dibujoDe` devuelve para cada símbolo del catálogo. Atarlo a
 * `Element` obligaba a la segunda a pasar por `renderToStaticMarkup` y
 * `DOMParser` —y entonces el escritor de la lámina sólo podría correr en el
 * navegador, nunca en el banco de pruebas, que es justo donde hace falta—.
 */
export interface NodoSVG {
  /** En minúscula: `path`, `rect`, `g`… */
  tag: string;
  attr: (nombre: string) => string | null;
  hijos: NodoSVG[];
}

/**
 * El motor: un árbol de nodos a un trazo encuadrado.
 *
 * Ignora a propósito el estilo —color, grosor, relleno—: el símbolo se pinta con
 * la tinta del plano (`currentColor`), igual que los 30 dibujados a mano, para
 * que una pieza importada no desentone al lado de una nativa ni se vuelva
 * invisible cuando el plano cambie de fondo.
 */
export function desdeArbol(raiz: NodoSVG): Trazo {
  const todos: Seg[] = [];
  const d: string[] = [];
  // Recorrido propio y no un selector plano: hay que acumular la matriz de cada
  // padre, y un selector pierde esa cadena.
  const caminar = (n: NodoSVG, m: Matriz) => {
    if (d.length > TOPE_CAMINOS) return;
    const mio = por(m, leerTransform(n.attr('transform')));
    if (PERMITIDAS.includes(n.tag)) {
      const segs = transformar(primitivaASegs(n.tag, n.attr), mio);
      if (segs.length) { todos.push(...segs); d.push(escribir(segs)); }
      return;
    }
    // `defs` y `symbol` no se dibujan solos —son plantillas para `use`, que no
    // seguimos—, así que entrar ahí traería piezas sueltas que nadie pidió.
    if (n.tag === 'defs' || n.tag === 'symbol' || n.tag === 'clippath') return;
    for (const h of n.hijos) caminar(h, mio);
  };
  caminar(raiz, IDENTIDAD);

  if (!d.length) throw new Error('El dibujo no trae ninguna línea que se pueda pintar.');
  if (d.length > TOPE_CAMINOS) {
    throw new Error(`El dibujo trae más de ${TOPE_CAMINOS} trazos: parece un plano entero y no un objeto.`);
  }
  const caja = cajaDe(todos);
  if (!caja) throw new Error('El dibujo no ocupa ningún espacio.');
  return { d, caja };
}

/** Un `Element` del navegador visto como `NodoSVG`. */
const comoNodo = (el: Element): NodoSVG => ({
  tag: el.tagName.toLowerCase(),
  attr: (a) => el.getAttribute(a),
  get hijos() { return Array.from(el.children).map(comoNodo); },
});

/** De un archivo SVG a un trazo encuadrado. */
export function desdeSVG(texto: string): Trazo {
  const doc = new DOMParser().parseFromString(texto, 'image/svg+xml');
  if (doc.querySelector('parsererror')) throw new Error('El archivo no es un SVG válido.');
  const raiz = doc.documentElement;
  if (raiz.tagName.toLowerCase() !== 'svg') throw new Error('El archivo no es un SVG.');
  return desdeArbol(comoNodo(raiz));
}

// ─── PDF ────────────────────────────────────────────────────────────────────

/**
 * De una página de PDF a un trazo encuadrado.
 *
 * Corre ENTERO en el navegador: `pdfjs-dist` ya es dependencia del frontend
 * —`EditorPlanta` la usa para el fondo del croquis—, así que importar un PDF no
 * necesita ni un servicio nuevo ni un despliegue.
 *
 * La lista de operaciones que devuelve pdf.js es la receta de dibujo de la
 * página. De ahí sólo interesa `constructPath`, cuya forma medimos contra 26 561
 * caminos reales antes de escribir esto: los argumentos son
 * `[opDePintado, [buffer], cajaMinMax]`, y el buffer es un chorro plano de
 * `[comando, …números]` con `0`=mover (2), `1`=recta (2), `2`=cúbica (6).
 */
export async function desdePDF(datos: ArrayBuffer, pagina = 1): Promise<Trazo> {
  const pdfjs = await import('pdfjs-dist');
  const doc = await pdfjs.getDocument({ data: new Uint8Array(datos) }).promise;
  if (pagina < 1 || pagina > doc.numPages) throw new Error(`El PDF no tiene página ${pagina}.`);
  const pag = await doc.getPage(pagina);
  const lista = await pag.getOperatorList();
  const OPS = pdfjs.OPS;

  // Se arranca con la matriz de la vista y no con la identidad: el PDF mide con
  // la Y hacia ARRIBA y el SVG hacia abajo. Empezar acá deja el dibujo derecho
  // sin un volteo propio después.
  let ctm = pag.getViewport({ scale: 1 }).transform as Matriz;
  const pila: Matriz[] = [];
  const todos: Seg[] = [];
  const d: string[] = [];

  for (let i = 0; i < lista.fnArray.length && d.length <= TOPE_CAMINOS; i++) {
    const fn = lista.fnArray[i];
    const args = lista.argsArray[i];
    if (fn === OPS.save) { pila.push(ctm); continue; }
    if (fn === OPS.restore) { ctm = pila.pop() ?? ctm; continue; }
    if (fn === OPS.transform) { ctm = por(ctm, args.slice(0, 6) as Matriz); continue; }
    if (fn !== OPS.constructPath) continue;

    // Un camino que termina en `endPath` o en un `clip` no se pinta: define un
    // recorte. El primero de casi todo PDF es el rectángulo de la página entera,
    // y colarlo haría que cada objeto importado naciera con un marco gigante que
    // aplasta el dibujo de verdad contra una esquina.
    const pinta = args[0];
    if (pinta === OPS.endPath || pinta === OPS.clip || pinta === OPS.eoClip) continue;

    const buf = args[1][0];
    const largo = buf.length ?? Object.keys(buf).length;
    const segs: Seg[] = [];
    for (let p = 0; p < largo;) {
      const c = buf[p];
      if (c === 0) { segs.push({ o: 'M', p: [buf[p + 1], buf[p + 2]] }); p += 3; }
      else if (c === 1) { segs.push({ o: 'L', p: [buf[p + 1], buf[p + 2]] }); p += 3; }
      else if (c === 2) {
        segs.push({ o: 'C', p: [buf[p + 1], buf[p + 2], buf[p + 3], buf[p + 4], buf[p + 5], buf[p + 6]] });
        p += 7;
      } else break; // comando desconocido: se corta este camino, no todo el PDF
    }
    const puestos = transformar(segs, ctm);
    if (puestos.length) { todos.push(...puestos); d.push(escribir(puestos)); }
  }

  if (!d.length) {
    throw new Error('El PDF no trae líneas vectoriales: parece una foto o un escaneo.');
  }
  if (d.length > TOPE_CAMINOS) {
    throw new Error(`El PDF trae más de ${TOPE_CAMINOS} trazos: recortá sólo el objeto y volvé a intentar.`);
  }
  const caja = cajaDe(todos);
  if (!caja) throw new Error('El dibujo del PDF no ocupa ningún espacio.');
  return { d, caja };
}

/** El lector que corresponde, elegido por el nombre del archivo. */
export async function desdeArchivo(f: File): Promise<Trazo> {
  if (/\.svg$/i.test(f.name)) return desdeSVG(await f.text());
  if (/\.pdf$/i.test(f.name)) return desdePDF(await f.arrayBuffer());
  throw new Error('Sólo se puede importar un SVG o un PDF.');
}
